import type { MetricEntry, RangePreset } from '../../api/metricsTypes.js';

export type BmiStatus = 'Underweight' | 'Normal' | 'Overweight';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function computeBmi(
  weightKg: number | null | undefined,
  heightCm: number | null | undefined,
): number | null {
  if (
    weightKg === null ||
    weightKg === undefined ||
    heightCm === null ||
    heightCm === undefined
  ) {
    return null;
  }
  if (!Number.isFinite(weightKg) || !Number.isFinite(heightCm)) return null;
  if (weightKg <= 0 || heightCm <= 0) return null;
  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);
  return Number.isFinite(bmi) ? bmi : null;
}

export function bmiStatus(bmi: number): BmiStatus {
  if (bmi < 18.5) return 'Underweight';
  if (bmi < 25) return 'Normal';
  return 'Overweight';
}

export function formatTrend(
  prev: number | null | undefined,
  latest: number | null | undefined,
): string | null {
  if (prev === null || prev === undefined) return null;
  if (latest === null || latest === undefined) return null;
  if (!Number.isFinite(prev) || !Number.isFinite(latest)) return null;
  const diff = Math.round((latest - prev) * 10) / 10;
  const rounded = Math.abs(diff).toFixed(1);
  if (diff > 0) return `+${rounded} kg`;
  if (diff < 0) return `-${rounded} kg`;
  return `±0.0 kg`;
}

function daysForPreset(preset: RangePreset): number | null {
  switch (preset) {
    case '1M':
      return 30;
    case '3M':
      return 90;
    case '6M':
      return 180;
    case 'All':
      return null;
  }
}

export function parseIsoDate(iso: string): number | null {
  if (typeof iso !== 'string') return null;
  const parts = iso.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts;
  const yn = Number(y);
  const mn = Number(m);
  const dn = Number(d);
  if (!Number.isFinite(yn) || !Number.isFinite(mn) || !Number.isFinite(dn)) {
    return null;
  }
  const t = new Date(yn, mn - 1, dn).getTime();
  return Number.isFinite(t) ? t : null;
}

export function filterByRange(
  entries: MetricEntry[],
  preset: RangePreset,
  now: number = Date.now(),
): MetricEntry[] {
  const sorted = [...entries].sort((a, b) => {
    const ta = parseIsoDate(a.measured_at) ?? 0;
    const tb = parseIsoDate(b.measured_at) ?? 0;
    return ta - tb;
  });
  const days = daysForPreset(preset);
  if (days === null) return sorted;
  const cutoff = now - days * MS_PER_DAY;
  return sorted.filter((e) => {
    const t = parseIsoDate(e.measured_at);
    return t !== null && t >= cutoff;
  });
}

export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isFutureIso(date: string, now: Date = new Date()): boolean {
  const t = parseIsoDate(date);
  if (t === null) return false;
  const todayEnd = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999,
  ).getTime();
  return t > todayEnd;
}

export function formatChartTick(iso: string): string {
  const t = parseIsoDate(iso);
  if (t === null) return iso;
  const d = new Date(t);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
